import React from 'react';
import styled from 'styled-components';
import PropTypes from 'prop-types';
import I10n from '../../features/i10n';
import Button from '../../atoms/button';
import MsxIcons from '../../features/msx-icons';
import DocumentationDropdown from '../documentation/documentation_dropdown';
import ExportLink from '../documentation/export_link';
import CustomerCenterButton from './customer_center_button';
import { buildCustomerCenterUrl } from '../../utils/url-builder';
import DynamicLabel from '../../atoms/dynamic-label';
import { PRIMARY_COLOR } from '../../color-constants';
import { appConfiguration } from '../../utils/app-starter';
import { BUY_AUTOMATION_URLS, LICENSE_MODELS } from '../../constants';
import InformationIcon from '../../atoms/information-icon';
import { logContactSalesEvent } from '../../utils/analytics-logger';

const BUY_AUTOMATION_TEXT = 'BuyAutomation';

const Container = styled.div`
  display: flex;
  flex-direction: column;
`;

const SavedTitleWrapper = styled.div`
  display: flex;
  align-items: center;
  justify-content: center;
`;

const SavedTitle = styled.span`
  font-size: 18px;
  font-weight: bold;
  color: #626469;
  margin-left: 5px;
`;

const ClipboardAndButtonsWrapper = styled.div`
  margin-bottom: 22px;
`;

const ButtonsWrapper = styled.div`
  display: flex;
  flex-direction: column;
`;

const MarginWrapper = styled.div`
  margin-top: 10px;
`;

const FullRowMarginWrapper = styled(MarginWrapper)`
  width: 100%;
`;

const WorkspaceCol = styled.div`
  display: flex;
  flex-direction: column;
  align-items: center;
  padding: 10px;
`;

const SavedIconAndLabelWrapper = styled.div`
  display: flex;
  align-items: center;
`;

const SavedConfigurationIcon = styled.div`
  color: ${PRIMARY_COLOR};
  color: var(--se-secondary, ${PRIMARY_COLOR});
`;

const SavedConfigurationLabel = styled.span`
  font-weight: 400;
  font-size: 18px;
  margin-left: 10px;
`;

const ViewMoreOptionsWrapper = styled.div`
  display: flex;
  align-items: center;
  margin-top: 10px;
  color: ${PRIMARY_COLOR};
  color: var(--se-secondary, ${PRIMARY_COLOR});
  cursor: pointer;
`;

const ViewMoreOptionsLabel = styled.span`
  font-size: 16px;
  font-weight: 400;
  margin-right: 10px;
`;

const OpenIconWrapper = styled.div`
  transform: ${props => props.isOpen ? 'rotate(-180deg)' : 'rotate(0deg)'};
`;

const MobileConfigurationNameEditor = styled.div`
  display: none;
  margin-bottom: 10px;
  @media (max-width: 800px) {
    display: block;
  }
`;

const BuyAutomationAccountWarningWrapper = styled.div`
  display: inline-block;
  margin-top: 10px;
`;

const InfoIconWrapper = styled.div`
  display: inline-block;
`;

const BuyAutomationAccountWarningLabel = styled.span`
  margin-left: 5px;
`;

const BuyAutomationLink = styled.a`
  margin-left: 5px;
`;

const SelectedIconWrapper = styled.div`
  display: flex;
  align-items: center;
`;

const DesktopButtonsWrapper = styled.div`
  display: block;
  @media (max-width: 800px) {
    display: none;
  }
`;

const MobileButtonsWrapper = styled.div`
  display: none;
  @media (max-width: 800px) {
    display: block;
  }
`;

const ShareSectionWrapper = styled.div`
  margin-bottom: 5px;
`;

class LicenceSavedConfigurationSection extends React.Component {

  static propTypes = {
    configuration: PropTypes.object,
    addToMyProduct: PropTypes.func,
    addToMyList: PropTypes.func,
    exportToXls: PropTypes.func,
    export: PropTypes.object,
    addToListLabel: PropTypes.string,
    showNotification: PropTypes.func,
    handleEmailFormOpen: PropTypes.func,
    shouldCallBuyAutomation: PropTypes.bool,
    buyAutomationAccountExists: PropTypes.bool,
    buyAutomationCheckingAccountLoading: PropTypes.bool,
    buyAutomationCartURL: PropTypes.string,
    buyAutomationAddToCartPerformed: PropTypes.bool,
    openModal: PropTypes.func,
    closeModal: PropTypes.func,
    isWrapperMode: PropTypes.bool,
    allowSavedModifications: PropTypes.bool,
    workspaceOptionsOpened: PropTypes.bool,
    toggleWorkspaceOptions: PropTypes.func,
    seamlessSession: PropTypes.object,
    launchFromWorkspace: PropTypes.bool,
    isWorkspaceAvailable: PropTypes.bool,
    unifiedCheckoutAvailable: PropTypes.bool,
    showDisclaimer: PropTypes.bool,
    onDisclaimerDismiss: PropTypes.func,
    isSaving: PropTypes.bool
  }

  copyToClipboard = text => {
    const el = document.createElement('textarea');
    el.value = text;
    document.body.appendChild(el);
    el.select();
    document.execCommand('copy');
    document.body.removeChild(el);
    this.props.showNotification(I10n.get('saved_configuration.copy_clipboard.notification'), 'success', () => null);
  }

  renderExportLinks = () => {
    const components = [];
    components.push(
      <ExportLink
        key={'saved_configuration.export.xls'}
        name={I10n.get('saved_configuration.export.xls')}
        onDownload={() => {
          this.props.exportToXls(this.props.configuration);
        }}
        loading={this.props.export.xls.isLoading}
        extensionLabel={".xls"}
      >
      </ExportLink>
    );

    return components;
  }

  renderShareLinks = () => {
    const components = [];
    components.push(
      <ExportLink
        key={'share.configuration-id'}
        name={I10n.get('saved_configuration.copy_conf_id')}
        onDownload={() => {
          this.copyToClipboard(this.props.configuration.id);
        }}
        loading={false}
        extensionLabel={""}
        hideIcon={true}
      >
      </ExportLink>
    );

    if (appConfiguration.locale !== 'zh-CN') {
      components.push(
        <ExportLink
          key={'share.configuration-id'}
          name={I10n.get('saved_configuration.send_by_mail')}
          onDownload={() => {
            this.props.handleEmailFormOpen(this.props.configuration);
          }}
          loading={false}
          extensionLabel={""}
          hideIcon={true}
        >
        </ExportLink>
      );
    }

    return components;
  }

  renderOptions = () => {
    const nbExports = 1;

    return (
      <>
        {(this.props.launchFromWorkspace || (this.props.unifiedCheckoutAvailable && this.props.isWrapperMode)) && <>
          <MobileConfigurationNameEditor>
            <DynamicLabel
              value={this.props.configuration.name}
              setValue={this.props.setConfigurationName}
              enableEdit={!this.props.isSaving}
              withCancel={true}
              customInputProps={{ name: "configurationName", maxLength: "20" }}
            >
            </DynamicLabel>
          </MobileConfigurationNameEditor>
        </>}
        <ShareSectionWrapper>
          <DocumentationDropdown defaultOpenStatus={window.innerWidth > 800}
            title={`${I10n.get('share_menu')}`}
            children={this.renderShareLinks()}>
          </DocumentationDropdown>
        </ShareSectionWrapper>
        <DocumentationDropdown defaultOpenStatus={window.innerWidth > 800}
          title={`${I10n.get('menu_export')} (${nbExports})`}
          children={this.renderExportLinks()}>
        </DocumentationDropdown>
      </>
    );
  }

  renderWorkspaceAddToCartButton = () => {
    return (
      <MarginWrapper>
        <Button importance="emphasized" expand={true} icon-name="plus-circle" disabled={this.props.export.my_product.isLoading || LICENSE_MODELS.SUBSCRIPTION === this.props.configuration.licenseModel} onClick={() => {  }} showDisclaimer={this.props.showDisclaimer}>
          {I10n.get('screen.licence_selector_save_screen.contact_customer_center')}
        </Button>
      </MarginWrapper>
    );
  }

  renderAddToCartButton = () => {
    let element = <></>;
    if (this.props.shouldCallBuyAutomation && (this.props.buyAutomationAccountExists || this.props.buyAutomationCheckingAccountLoading)) {
      element = (
        <Button expand={true} importance="emphasized" primary disabled={this.props.export.my_product.isLoading || this.props.buyAutomationCheckingAccountLoading || this.props.buyAutomationAddToCartPerformed} onClick={() => {
            this.props.addToBuyAutomation(this.props.configuration);
          }}>
          {I10n.get('saved_configuration.export.buy_automation')}
        </Button>
      );
    } else if (appConfiguration.unifiedCheckoutAvailable && LICENSE_MODELS.SUBSCRIPTION !== this.props.configuration.licenseModel && !this.props.shouldCallBuyAutomation) {
      element = (
        <Button expand={true} importance="emphasized" icon-name="shopping-cart" primary disabled={this.props.export.my_product.isLoading} onClick={() => {
            this.props.addToMyProduct(this.props.configuration);
          }}>
          {I10n.get('saved_configuration.export.add_to_my_cart')}
        </Button>
      );

    } else if ((appConfiguration.isWorkspaceAvailable || appConfiguration.launchFromWorkspace) && LICENSE_MODELS.SUBSCRIPTION !== this.props.configuration.licenseModel  && !this.props.shouldCallBuyAutomation) {
      element = (
        <Button expand={true} importance="emphasized" icon-name="bag-plus" primary disabled={this.props.export.my_product.isLoading} onClick={() => {
            this.props.addToMyProduct(this.props.configuration);
          }}>
          {I10n.get('saved_configuration.export.my_project')}
        </Button>
      );
    } else {
      element = (
        <>
          <CustomerCenterButton expand={true} software={this.props.configuration.softwareSelection} disabled={this.props.export.my_product.isLoading} isLoading={this.props.export.my_product.isLoading} onClick={() => {
            window.open(buildCustomerCenterUrl(), '_blank');
            logContactSalesEvent(this.props.configuration, this.props.configuration.step);
          }} showDisclaimer={this.props.showDisclaimer} onDisclaimerDismiss={this.props.onDisclaimerDismiss}></CustomerCenterButton>
        </>
      );

    }

    return (
      <MarginWrapper>
        {element}
      </MarginWrapper>
    );
  }

  renderCreateBuyAutomationAccountWarning = () => {
    const environment = process.env.REACT_APP_SEAMLESS_ENV || 'prod';
    const buyAutomationURL = BUY_AUTOMATION_URLS[environment]?.APP_URL || BUY_AUTOMATION_URLS.prod.APP_URL;
    let showClickHere = false;
    let warningLabel = I10n.get('create_buy_automation_account');
    if (!warningLabel.includes(BUY_AUTOMATION_TEXT)) {
      showClickHere = true;
    } else {
      warningLabel = warningLabel.replace(BUY_AUTOMATION_TEXT, `<a target="_blank" href="${buyAutomationURL}">${BUY_AUTOMATION_TEXT}</a>`);
    }

    return (
      <BuyAutomationAccountWarningWrapper>
        <InfoIconWrapper>
          <InformationIcon size="small"></InformationIcon>
        </InfoIconWrapper>
        <BuyAutomationAccountWarningLabel dangerouslySetInnerHTML={{ __html: warningLabel }}></BuyAutomationAccountWarningLabel>
        {showClickHere && <BuyAutomationLink target="_blank" href={buyAutomationURL}>{I10n.get('app.action.click_here')}</BuyAutomationLink>}
      </BuyAutomationAccountWarningWrapper>
    );
  }

  renderViewBuyAutomationProducts = () => {
    let showClickHere = false;
    let warningLabel = I10n.get('buy_automation.view_products');
    if (!warningLabel.includes(BUY_AUTOMATION_TEXT)) {
      showClickHere = true;
    } else {
      warningLabel = warningLabel.replace(BUY_AUTOMATION_TEXT, `<a target="_blank" href="${this.props.buyAutomationCartURL}">${BUY_AUTOMATION_TEXT}</a>`);
    }

    return (
      <BuyAutomationAccountWarningWrapper>
        <InfoIconWrapper>
          <InformationIcon size="small"></InformationIcon>
        </InfoIconWrapper>
        <BuyAutomationAccountWarningLabel dangerouslySetInnerHTML={{ __html: warningLabel }}></BuyAutomationAccountWarningLabel>
        {showClickHere && <BuyAutomationLink target="_blank" href={this.props.buyAutomationCartURL}>{I10n.get('app.action.click_here')}</BuyAutomationLink>}
      </BuyAutomationAccountWarningWrapper>
    );
  }

  renderButtons = () => {
    return (
      <>
        <MobileConfigurationNameEditor>
          <DynamicLabel
            value={this.props.configuration.name}
            setValue={this.props.setConfigurationName}
            enableEdit={!this.props.isSaving}
            withCancel={true}
            customInputProps={{ name: "configurationName", maxLength: "20" }}
          >
          </DynamicLabel>
        </MobileConfigurationNameEditor>
        <ClipboardAndButtonsWrapper>
          {this.props.allowSavedModifications && <SavedTitleWrapper>
            <SelectedIconWrapper>
              <MsxIcons name="selected" color="primary" size="large"></MsxIcons>
            <SavedTitle>{I10n.get('saved_configuration.configuration_saved')}</SavedTitle>
            </SelectedIconWrapper>

          </SavedTitleWrapper>}
          <ButtonsWrapper>
            {this.props.unifiedCheckoutAvailable && LICENSE_MODELS.SUBSCRIPTION !== this.props.configuration.licenseModel && !this.props.shouldCallBuyAutomation && <MarginWrapper>
              <Button expand={true} icon-name="list-bullets" onClick={() => {
                this.props.addToMyList(this.props.configuration);
              }}>
                {this.props.addToListLabel ? this.props.addToListLabel : I10n.get('saved_configuration.export.add_to_my_list')}
              </Button>
            </MarginWrapper>}
            {this.renderAddToCartButton()}
          </ButtonsWrapper>
          {this.props.shouldCallBuyAutomation && !this.props.buyAutomationCheckingAccountLoading && !this.props.buyAutomationAccountExists && this.renderCreateBuyAutomationAccountWarning()}
          {this.props.shouldCallBuyAutomation && !this.props.buyAutomationCheckingAccountLoading && this.props.buyAutomationAddToCartPerformed && this.renderViewBuyAutomationProducts()}
        </ClipboardAndButtonsWrapper>
      </>
    );

  }

  renderDefaultSavedSection = () => {
    return (
      <>
        <DesktopButtonsWrapper>
          {this.renderButtons()}
        </DesktopButtonsWrapper>
        <MobileButtonsWrapper>
          {this.renderButtons()}
        </MobileButtonsWrapper>
      </>
    );
  }

  renderWorkspaceSavedSection = () => {
    let addToMyProjectLabel = I10n.get('saved_configuration.export.my_project');
    let addToMyProjectIcon = 'plus-circle';
    if (this.props.unifiedCheckoutAvailable) {
      addToMyProjectLabel = I10n.get('saved_configuration.export.add_to_my_cart');
      addToMyProjectIcon = 'shopping-cart';
    }
    if (this.props.addToMyProductLabel) {
      addToMyProjectLabel = this.props.addToMyProductLabel;
    }
    return (<WorkspaceCol>
      {this.props.allowSavedModifications && <SavedIconAndLabelWrapper>
        <SavedConfigurationIcon>
          <MsxIcons name="selected"></MsxIcons>
        </SavedConfigurationIcon>
        <SavedConfigurationLabel>{I10n.get('saved_configuration.configuration_saved')}</SavedConfigurationLabel>
      </SavedIconAndLabelWrapper>}
      {this.props.unifiedCheckoutAvailable && LICENSE_MODELS.SUBSCRIPTION !== this.props.configuration.licenseModel &&
        <FullRowMarginWrapper>
          <Button expand={true} icon-name="list-bullets" onClick={() => {
            this.props.addToMyList(this.props.configuration);
          }}>
            {this.props.addToListLabel ? this.props.addToListLabel : I10n.get('saved_configuration.export.add_to_my_list')}
          </Button>
        </FullRowMarginWrapper>
      }
      <FullRowMarginWrapper>
        <Button importance="emphasized" expand={true} icon-name={addToMyProjectIcon} disabled={this.props.export.my_product.isLoading || LICENSE_MODELS.SUBSCRIPTION === this.props.configuration.licenseModel} onClick={() => {
            this.props.addToMyProduct(this.props.configuration);
        }}>
          {addToMyProjectLabel}
        </Button>
      </FullRowMarginWrapper>
      <ViewMoreOptionsWrapper onClick={this.props.toggleWorkspaceOptions}>
        <ViewMoreOptionsLabel>{I10n.get('saved_configuration.view_more_options')}</ViewMoreOptionsLabel>
        <OpenIconWrapper isOpen={this.props.workspaceOptionsOpened} >
          <MsxIcons name="down-arrow" size="small"></MsxIcons>
        </OpenIconWrapper>
      </ViewMoreOptionsWrapper>
    </WorkspaceCol>);
  }

  render = () => {
    return (
      <Container>
        {(this.props.launchFromWorkspace || (this.props.unifiedCheckoutAvailable && this.props.isWrapperMode)) ? this.renderWorkspaceSavedSection() : this.renderDefaultSavedSection()}
        {((!this.props.launchFromWorkspace && !(this.props.unifiedCheckoutAvailable && this.props.isWrapperMode)) || this.props.workspaceOptionsOpened) && this.renderOptions()}
      </Container>
    );
  };

}
export default LicenceSavedConfigurationSection;

